import '../../index.css'
import React, { useState, useEffect } from "react"
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Slider
} from '@mui/material'
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api'
import { HOST } from '../../constants'

const containerStyle = {
  width: '100%',
  height: '600px'
};

// Approx center of Brazil
const center = { lat: -14.2350, lng: -51.9253 };

// A simple helper function to calculate distance in kilometers between
// two lat/lng points using the Haversine formula (or a similarly appropriate formula).
function getDistanceFromLatLonInM(lat1, lon1, lat2, lon2) {
  // Radius of the earth in m
  const R = 6371
  const dLat = deg2rad(lat2 - lat1)
  const dLon = deg2rad(lon2 - lon1)
  const a =
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
    Math.sin(dLon/2) * Math.sin(dLon/2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))
  const d = R * c
  return d * 1000 // in meters
}

function deg2rad(deg) {
  return deg * (Math.PI/180)
}

const WorldView = () => {
  const [devices, setDevices] = useState([]);
  const [error, setError] = useState({ error: false, message: '' });
  const [filterStatus, setFilterStatus] = useState('all');

  // For distance filter
  const [enableDistanceFilter, setEnableDistanceFilter] = useState(false)
  const [distanceThreshold, setDistanceThreshold] = useState(100) // 100m default

  // State to track which device is selected (for InfoWindow)
  const [selectedDevice, setSelectedDevice] = useState(null);

  useEffect(() => {
    fetch(HOST + '/devices/lastLocation', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        token: JSON.parse(localStorage.getItem('user_data')).token
      })
    })
      .then((response) => {
        if (response.status !== 200 && response.status !== 400) {
          setError({ error: true, message: 'Erro de comunicação com o servidor' })
        } else {
          response.json().then((data) => {
            if (data.success === false && data.message) {
              setError({ error: true, message: data.message })
            } else {
              setDevices(data.data);
              setError({ error: false, message: '' });
            }
          })
        }
      })
      .catch(() => {
        setError({ error: true, message: 'Erro de comunicação com o servidor' })
      })
  }, []);

  // Filter devices by status
  const filteredByStatus = devices.filter(device => {
    if (filterStatus === 'all') return true;
    return device.status === filterStatus;
  });

  // If distance filter is enabled, filter out devices that do NOT
  // have at least one neighbor within "distanceThreshold" kilometers.
  const filteredByDistance = enableDistanceFilter
    ? filteredByStatus.filter((device, _, arr) => {
        const lat1 = parseFloat(device.latitude)
        const lon1 = parseFloat(device.longitude)

        // if current device doesn't have valid coords, skip it
        if (isNaN(lat1) || isNaN(lon1)) return false

        // Check if there's another device in the array that is "close enough"
        return arr.some(other => {
          if (other.imei === device.imei) return false // skip itself
          const lat2 = parseFloat(other.latitude)
          const lon2 = parseFloat(other.longitude)
          if (isNaN(lat2) || isNaN(lon2)) return false

          // Calculate distance
          const distance = getDistanceFromLatLonInM(lat1, lon1, lat2, lon2)
          return distance <= distanceThreshold
        })
      })
    : filteredByStatus

  const genModelString = (device) => {
    let data = JSON.parse(device.json_data);
    return data['manufacturer']+ ' ' + data['model'] + ' ' + data['device'] + ' ' + data['serial']
  }

  return (
    <React.Fragment>
      <Box marginLeft="20px" marginRight="20px" marginTop="20px">
        {/* Error message */}
        {error.error && (
          <div style={{ color: 'red', margin: '10px 0' }}>
            {error.message}
          </div>
        )}

        {/* Filter Dropdown */}
        <FormControl sx={{ minWidth: 120, marginBottom: 2, marginRight: 2 }}>
          <InputLabel id="filter-status-label">Status</InputLabel>
          <Select
            labelId="filter-status-label"
            label="Status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="online">Online</MenuItem>
            <MenuItem value="offline">Offline</MenuItem>
          </Select>
        </FormControl>

        {/* Distance filter checkbox */}
        <FormControlLabel
          control={
            <Checkbox
              checked={enableDistanceFilter}
              onChange={(e) => setEnableDistanceFilter(e.target.checked)}
            />
          }
          label="Filtrar por distância"
          style={{ marginRight: 20 }}
            className="distance-slider"
        />

        {/* Distance slider (only visible if checkbox is enabled) */}
        {enableDistanceFilter && (
          <Box width={200} display="inline-block" verticalAlign="middle">
            <Slider
              className="distance-slider"
              value={distanceThreshold}
              min={1}
              max={100}
              step={1}
              valueLabelDisplay="auto"
              onChange={(e, newValue) => setDistanceThreshold(newValue)}
            />
            <div>Distância: {distanceThreshold} m</div>
          </Box>
        )}

        {/* Google Map Section */}
        <LoadScript googleMapsApiKey="AIzaSyCCGphF1SBfCe9ldJt2ETOaGvHwbAzk28o">
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={4}
            mapTypeId="terrain"
          >
            {filteredByDistance.map((device, index) => {
              const lat = parseFloat(device.latitude);
              const lng = parseFloat(device.longitude);

              if (isNaN(lat) || isNaN(lng)) return null;

              return (
                <Marker
                  key={index}
                  position={{ lat, lng }}
                  onClick={() => setSelectedDevice(device)} // select this device on marker click
                />
              );
            })}

            {/* InfoWindow for the selected device */}
            {selectedDevice && (
              <InfoWindow
                position={{
                  lat: parseFloat(selectedDevice.latitude),
                  lng: parseFloat(selectedDevice.longitude),
                }}
                onCloseClick={() => setSelectedDevice(null)}
              >
                <div className="maps-info-window" style={{ color: 'black' }}>
                  <h3>IMEI: {selectedDevice.imei}</h3>
                  <p>Proprietário: {selectedDevice.owner ? selectedDevice.owner : 'Sem proprietário'}</p>
                  <p>{genModelString(selectedDevice)}</p>
                  <p>Status: {selectedDevice.status}</p>
                  <a href={`/device/${selectedDevice.imei}`} style={{ color: 'blue' }}>
                    Detalhes do Dispositivo
                  </a>
                </div>
              </InfoWindow>
            )}
          </GoogleMap>
        </LoadScript>
      </Box>
    </React.Fragment>
  )
}

export default WorldView
